import { createStyles, makeStyles, Theme, Tabs, Tab } from '@material-ui/core';
import React from 'react';
import { StyledTabContainer, StyledTableWrapper } from './_components';
import { connect } from 'react-redux';
import { ContestApiResponse, LineupApiResponse } from './queries/queryTypes';
import LineupFilter from '../common/Table/lineupFilter';
import { APIPagination, APIPaginationResponse, Filter, Filters, MultipleFilters, Pages } from './store/types';
import { setFilter, setPageInfo, setUser } from './store/actions';
import { Constants } from '../postContest/store/constants';
import { APIState } from '../utils/api/reducer';
import { APIConstants } from '../utils/api/constants';
import DataGrid from '../common/Table/newTable';
import {
  postContestUserDataGridHeaderConfig,
  postContestUserWithActualResultsDataGridHeaderConfig,
  postContestPlayerDataGridHeaderConfig,
  postContestPlayerWithActualResultsDataGridHeaderConfig,
  postContestPlayerUserDataGridHeaderConfig,
  postContestPlayerUserWithActualResultsDataGridHeaderConfig,
  postContestLineupDataGridHeaderConfig,
  postContestLineupWithActualResultsDataGridHeaderConfig,
} from '../common/Table/PostContestConfig';
import { GridColDef } from '@mui/x-data-grid';
import { getPostContestStackTableConfig, showStackPostContest } from '../utils/stackConstants';
import { useUserId } from '../utils/authentication';

const useStyles = makeStyles((theme: Theme) => createStyles({
  tab: {
    marginRight: '15px',
    borderRadius: "3px 3px 0px 0px",
    fontSize: '12px',
    fontWeight: 400,
    fontFamily: 'Inter',
  },
  active: {
    fontWeight: 800,
  },
})
);

interface ReduxProps {
  readonly selectedContest: ContestApiResponse;
  readonly enabledContest: ContestApiResponse;
  readonly selectedUser: string,
  readonly selectedOpponent: string,
  readonly filters: Filters,
  readonly pageInfo: Pages,
  readonly userRoi: APIState<LineupApiResponse[]>,
  readonly userNameRoi: APIState<LineupApiResponse[]>,
  readonly playerAllRoi: APIState<LineupApiResponse[]>,
  readonly playerUserRoi: APIState<LineupApiResponse[]>,
  readonly playerOpponentRoi: APIState<LineupApiResponse[]>,
  readonly lineupAllRoi: APIState<LineupApiResponse[]>,
  readonly lineupUserRoi: APIState<LineupApiResponse[]>,
  readonly lineupOpponentRoi: APIState<LineupApiResponse[]>,
  readonly stackRoiAll: APIState<LineupApiResponse[]>,
  readonly stackRoiUser: APIState<LineupApiResponse[]>,
  readonly stackRoiOpp: APIState<LineupApiResponse[]>,
  readonly pagination: APIPagination;
}

interface DispatchProps {
  readonly SetFilter: (filter: Filter | MultipleFilters, type: Constants.SET_USER_SORT | Constants.SET_PLAYER_SORT | Constants.SET_LINEUP_SORT | Constants.SET_STACK_SORT) => void;
  readonly SetPageInfo: (pageInfo: Pages) => void;
  readonly SetUser: (user: string) => void;
}

type Props = ReduxProps & DispatchProps;

const TabsWrapper = (props: Props) => {
  const classes = useStyles();
  const [value, setValue] = React.useState('user');
  const [filter, setFilter] = React.useState('all');
  const [lineupTabData, setLineupTabData] = React.useState<LineupApiResponse[]>([]);
  const [playerTabData, setPlayerTabData] = React.useState<LineupApiResponse[]>([]);
  const [stackTabData, setStackTabData] = React.useState<LineupApiResponse[]>([]);
  const [stackType, setStackType] = React.useState<string>('playerExp');
  const [userActualResultsAvailable, setUserActualResultsAvailable] = React.useState<boolean>(false);
  const [playerHeaders, setPlayerHeaders] = React.useState<GridColDef[]>(postContestPlayerDataGridHeaderConfig);
  const [lineupActualResultsAvailable, setLineupActualResultsAvailable] = React.useState<boolean>(false);
  const userId = useUserId();
  const auth = 'awe' + userId;

  const {
    selectedContest,
    enabledContest,
    selectedUser,
    selectedOpponent,
    userRoi,
    playerAllRoi,
    playerUserRoi,
    playerOpponentRoi,
    lineupAllRoi,
    lineupUserRoi,
    lineupOpponentRoi,
    userNameRoi,
    pagination,
    stackRoiAll,
    stackRoiUser,
    stackRoiOpp,
    SetUser,
  } = props;

  const lineupData: { [key: string]: { roi: APIState<LineupApiResponse[]>, pagination: APIPaginationResponse } } = {
    'all': { roi: lineupAllRoi, pagination: pagination.lineup.all },
    'user': { roi: lineupUserRoi, pagination: pagination.lineup.user },
    'opp': { roi: lineupOpponentRoi, pagination: pagination.lineup.opponent },
  };

  const playerData: { [key: string]: { roi: APIState<LineupApiResponse[]>, pagination: APIPaginationResponse } } = {
    'all': { roi: playerAllRoi, pagination: pagination.player.all },
    'user': { roi: playerUserRoi, pagination: pagination.player.user },
    'opp': { roi: playerOpponentRoi, pagination: pagination.player.opponent },
  };

  const stackData: { [key: string]: { roi: APIState<LineupApiResponse[]>, pagination: APIPaginationResponse } } = {
    'all': { roi: stackRoiAll, pagination: pagination.stack.all },
    'user': { roi: stackRoiUser, pagination: pagination.stack.user },
    'opp': { roi: stackRoiOpp, pagination: pagination.stack.opponent },
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: any) => {
    if(newValue === 'user'){
      setFilter('all');
    }
    setValue(newValue);
  };

  React.useEffect(() => {
    setLineupTabData(lineupData[filter]?.roi.data);
    setPlayerTabData(playerData[filter]?.roi.data);
    setStackTabData(stackData[filter]?.roi.data);
  }, [filter, lineupData, playerData]);

  React.useEffect(() => {
    setFilter('all');
  }, [selectedContest]);

  React.useEffect(() => {
    if (userRoi.status === APIConstants.SUCCEEDED) {
      setUserActualResultsAvailable(userRoi?.data[0]?.actualLineupRoiPct !== undefined);
    }
  }, [userRoi]);

  React.useEffect(() => {
    if (lineupData[filter].roi.status === APIConstants.SUCCEEDED) {
      setLineupActualResultsAvailable(lineupTabData[0]?.actualLineupRoiPct !== undefined);
    }
  }, [lineupTabData]);

  React.useEffect(() => {
    if (playerData[filter].roi.status === APIConstants.SUCCEEDED) {
      if (['user', 'opp'].indexOf(filter) > -1) {
        if (playerTabData[0]?.actualPlayerRoiPct !== undefined) {
          setPlayerHeaders(postContestPlayerUserWithActualResultsDataGridHeaderConfig(filter === 'user' ? selectedUser : selectedOpponent, filter === 'user' ? selectedOpponent : selectedUser))
        } else {
          setPlayerHeaders(postContestPlayerUserDataGridHeaderConfig(filter === 'user' ? selectedUser : selectedOpponent, filter === 'user' ? selectedOpponent : selectedUser));
        }
      }
      else {
        if (playerTabData[0]?.actualPlayerRoiPct !== undefined) {
          setPlayerHeaders(postContestPlayerWithActualResultsDataGridHeaderConfig);
        } else {
          setPlayerHeaders(postContestPlayerDataGridHeaderConfig);
        }
      }
    }
  }, [playerTabData]);

  const stackTypeRois = () => {
    var stackTypes = stackTabData[0]?.stackTypeRois.map(x => ({...x, id: x.stackType}));
    return stackTypes;
  }

  const handleUserClick = (user: string) => {
    SetUser(user);
    setValue('lineup');
    setFilter('user');
  }

  return (
    <>
      <StyledTabContainer>
        <Tabs
          value={value}
          indicatorColor="primary"
          textColor="inherit"
          onChange={handleChange}
        >
          <Tab value='user' label="User ROI" className={`${classes.tab} ${value === 'user' ? classes.active : ''}`}></Tab>
          <Tab value='player' label="Player ROI" className={`${classes.tab} ${value === 'player' ? classes.active : ''}`}></Tab>
          {
            showStackPostContest(enabledContest) && (
              <Tab value='stack' label="Stack ROI" className={`${classes.tab} ${value === 'stack' ? classes.active : ''}`}></Tab>
            )
          }
          <Tab value='lineup' label="Lineup ROI" className={`${classes.tab} ${value === 'lineup' ? classes.active : ''}`}></Tab>
        </Tabs>
        <StyledTableWrapper>
          {
            userRoi.data && userRoi.data?.length > 0 && (
              <LineupFilter
                user={userRoi.status !== APIConstants.UNSTARTED && selectedUser ? selectedUser : ''}
                opponent={lineupOpponentRoi.status !== APIConstants.UNSTARTED && selectedOpponent ? selectedOpponent : ''}
                view={value}
                filter={filter}
                onSelect={(f: string) => setFilter(f)} />
            )
          }
          {
            value === 'user' && (
              <DataGrid
                includeStacks={showStackPostContest(enabledContest)}
                lineups={filter === 'user' ? userNameRoi.data : userRoi.data}
                headers={userActualResultsAvailable ? postContestUserWithActualResultsDataGridHeaderConfig(handleUserClick) : postContestUserDataGridHeaderConfig(handleUserClick)}
                isLoading={userRoi.status === APIConstants.STARTED}
                selectedView='user'
                setPlayerList={() => console.log('')}
                setStackList={() => console.log('')}
                searchedUser={selectedUser}
                simType={'post' + auth?.toLowerCase()}
                setStackView={(view: string) => console.log('')}
                backgroundColor='rgba(1, 90, 206, 0.15)'
              />
            )
          }
          {
            value === 'player' && (
              <DataGrid
                includeStacks={showStackPostContest(enabledContest)}
                lineups={playerTabData}
                headers={playerHeaders}
                isLoading={playerData[filter]?.roi.status === APIConstants.STARTED}
                selectedView='player'
                setPlayerList={() => console.log('')}
                setStackList={() => console.log('')}
                simType={'post' + auth?.toLowerCase()}
                setStackView={(view: string) => console.log('')}
                backgroundColor='rgba(1, 90, 206, 0.15)'
              />
            )
          }
          {
            value === 'stack' && (
              <DataGrid
                includeStacks={showStackPostContest(enabledContest)}
                lineups={stackType === 'playerExp' ? stackTabData : stackTypeRois()}
                headers={getPostContestStackTableConfig(enabledContest, stackType)}
                isLoading={stackData[filter]?.roi.status === APIConstants.STARTED}
                selectedView={enabledContest?.sport?.toUpperCase() === 'MLB' ? stackType : 'stack'}
                setPlayerList={() => console.log('')}
                setStackList={() => console.log('')}
                simType={'post' + auth?.toLowerCase()}
                setStackView={(view: string) => setStackType(view)}
                backgroundColor='rgba(1, 90, 206, 0.15)'
              />
            )
          }
          {
            value === 'lineup' && (
              <DataGrid
                includeStacks={showStackPostContest(enabledContest)}
                lineups={lineupTabData}
                headers={lineupActualResultsAvailable ? postContestLineupWithActualResultsDataGridHeaderConfig : postContestLineupDataGridHeaderConfig}
                isLoading={lineupData[filter]?.roi.status === APIConstants.STARTED}
                selectedView='lineup'
                setPlayerList={() => console.log('')}
                setStackList={() => console.log('')}
                simType={'post' + auth?.toLowerCase()}
                setStackView={(view: string) => console.log('')}
                backgroundColor='rgba(1, 90, 206, 0.15)'
              />
            )
          }
        </StyledTableWrapper>
      </StyledTabContainer>
    </>
  )
}
const mapStateToProps = (state: any) => {
  const {
    selectedContest,
    selectedUser,
    selectedOpponent,
    filters,
    pageInfo,
    userRoi,
    userNameRoi,
    playerAllRoi,
    playerUserRoi,
    playerOpponentRoi,
    lineupAllRoi,
    lineupUserRoi,
    lineupOpponentRoi,
    stackRoiAll,
    stackRoiUser,
    stackRoiOpp,
    pagination,
    enabledContest,
  } = state.postContest;
  return {
    selectedContest,
    selectedUser,
    selectedOpponent,
    filters,
    pageInfo,
    userRoi,
    userNameRoi,
    playerAllRoi,
    playerUserRoi,
    playerOpponentRoi,
    lineupAllRoi,
    lineupUserRoi,
    lineupOpponentRoi,
    pagination,
    stackRoiAll,
    stackRoiUser,
    stackRoiOpp,
    enabledContest
  }
};

const mapDispatchToProps = {
  SetFilter: setFilter,
  SetPageInfo: setPageInfo,
  SetUser: setUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(TabsWrapper);